import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import {
  Leaf,
  TrendingUp,
  Target,
  Award,
  RefreshCw,
  Plus,
  ChevronRight,
  ChevronDown,
  CheckCircle,
  Users,
  Building,
  Euro,
  Trash2,
  Edit,
  BarChart3,
  Download,
  FileSpreadsheet,
  FileText,
  Calendar,
  Wallet,
  Settings,
  X,
  Save,
  AlertTriangle,
  Clock
} from 'lucide-react';
import {
  ResponsiveContainer,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid
} from 'recharts';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const RSE = () => {
  const [dashboard, setDashboard] = useState(null);
  const [objectives, setObjectives] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedObjectives, setExpandedObjectives] = useState(new Set());
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [showEditActionDialog, setShowEditActionDialog] = useState(false);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [selectedObjective, setSelectedObjective] = useState(null);
  const [editingObjective, setEditingObjective] = useState(null);
  const [editingAction, setEditingAction] = useState(null);
  const [editingActionIndex, setEditingActionIndex] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');

  const emptyObjective = {
    categorie: 'environnement',
    titre: '',
    description: '',
    indicateur: '',
    unite: '%',
    valeur_cible: 100,
    valeur_actuelle: 0,
    date_debut: '',
    date_fin: '',
    responsable: '',
    responsable_id: '',
    priorite: 'medium',
    statut: 'en_cours',
    budget: 0,
    budget_utilise: 0
  };

  const [newObjective, setNewObjective] = useState({ ...emptyObjective });

  const [newAction, setNewAction] = useState({
    titre: '',
    description: '',
    echeance: '',
    responsable: ''
  });

  const [editCategories, setEditCategories] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const [dashboardRes, objectivesRes, categoriesRes, employeesRes] = await Promise.all([
        axios.get(`${API_URL}/rse/dashboard`).catch(() => ({ data: null })),
        axios.get(`${API_URL}/rse/objectives`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/rse/categories`).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/employees`).catch(() => ({ data: [] }))
      ]);
      setDashboard(dashboardRes.data);
      setObjectives(objectivesRes.data || []);
      setCategories(categoriesRes.data || {});
      setEmployees(employeesRes.data || []);
    } catch (error) {
      console.error('Error fetching RSE data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // === CRUD Objectifs ===

  const handleCreateObjective = async () => {
    if (!newObjective.titre) {
      toast.error('Le titre est requis');
      return;
    }
    try {
      await axios.post(`${API_URL}/rse/objectives`, newObjective);
      toast.success('Objectif RSE créé');
      setShowAddDialog(false);
      setNewObjective({ ...emptyObjective });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleEditObjective = async () => {
    if (!editingObjective || !editingObjective.titre) {
      toast.error('Le titre est requis');
      return;
    }
    try {
      await axios.put(`${API_URL}/rse/objectives/${editingObjective.id}`, editingObjective);
      toast.success('Objectif mis à jour');
      setShowEditDialog(false);
      setEditingObjective(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteObjective = async (objectiveId) => {
    if (!window.confirm('Supprimer cet objectif et toutes ses actions ?')) return;
    try {
      await axios.delete(`${API_URL}/rse/objectives/${objectiveId}`);
      toast.success('Objectif supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // === CRUD Actions ===

  const handleAddAction = async () => {
    if (!selectedObjective || !newAction.titre) {
      toast.error("Le titre de l'action est requis");
      return;
    }
    try {
      await axios.post(`${API_URL}/rse/objectives/${selectedObjective.id}/actions`, newAction);
      toast.success('Action ajoutée');
      setShowActionDialog(false);
      setNewAction({ titre: '', description: '', echeance: '', responsable: '' });
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de l'ajout");
    }
  };

  const handleToggleAction = async (objectiveId, actionIndex, completed) => {
    try {
      await axios.put(`${API_URL}/rse/objectives/${objectiveId}/actions/${actionIndex}`, { completed });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleEditAction = async () => {
    if (!editingAction || !editingAction.titre) {
      toast.error("Le titre de l'action est requis");
      return;
    }
    try {
      await axios.put(
        `${API_URL}/rse/objectives/${selectedObjective.id}/actions/${editingActionIndex}/edit`,
        editingAction
      );
      toast.success('Action modifiée');
      setShowEditActionDialog(false);
      setEditingAction(null);
      setEditingActionIndex(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const handleDeleteAction = async (objectiveId, actionIndex) => {
    if (!window.confirm('Supprimer cette action ?')) return;
    try {
      await axios.delete(`${API_URL}/rse/objectives/${objectiveId}/actions/${actionIndex}`);
      toast.success('Action supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // === Catégories ===

  const handleSaveCategories = async () => {
    try {
      await axios.put(`${API_URL}/rse/categories`, editCategories);
      toast.success('Catégories mises à jour');
      setShowCategoryDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  // === Export ===

  const handleExport = async (format) => {
    try {
      const response = await axios.get(`${API_URL}/rse/export/${format}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', format === 'csv' ? 'rse_export.csv' : 'rapport_rse.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Export ${format.toUpperCase()} téléchargé`);
    } catch (error) {
      toast.error("Erreur lors de l'export");
    }
  };

  // === Helpers ===

  const toggleExpand = (objectiveId) => {
    const newExpanded = new Set(expandedObjectives);
    if (newExpanded.has(objectiveId)) {
      newExpanded.delete(objectiveId);
    } else {
      newExpanded.add(objectiveId);
    }
    setExpandedObjectives(newExpanded);
  };

  const getCategoryIcon = (categoryCode) => {
    switch (categoryCode) {
      case 'environnement': return <Leaf className="w-5 h-5" />;
      case 'social': return <Users className="w-5 h-5" />;
      case 'gouvernance': return <Building className="w-5 h-5" />;
      case 'economie': return <Euro className="w-5 h-5" />;
      default: return <Target className="w-5 h-5" />;
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'critical': return <Badge className="bg-red-100 text-red-800">Critique</Badge>;
      case 'high': return <Badge className="bg-orange-100 text-orange-800">Haute</Badge>;
      case 'medium': return <Badge className="bg-yellow-100 text-yellow-800">Moyenne</Badge>;
      default: return <Badge className="bg-slate-100 text-slate-800">Basse</Badge>;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'termine': return <Badge className="bg-emerald-100 text-emerald-800">Terminé</Badge>;
      case 'en_cours': return <Badge className="bg-blue-100 text-blue-800">En cours</Badge>;
      case 'planifie': return <Badge className="bg-slate-100 text-slate-800">Planifié</Badge>;
      case 'abandonne': return <Badge className="bg-red-100 text-red-800">Abandonné</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getUpcomingDeadlines = () => {
    const now = new Date();
    const deadlines = [];
    objectives.forEach(obj => {
      (obj.actions || []).forEach((action, idx) => {
        if (action.echeance && !action.completed) {
          const date = new Date(action.echeance);
          const daysLeft = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
          if (daysLeft <= 30 && daysLeft >= 0) {
            deadlines.push({
              objectiveTitle: obj.titre,
              objectiveId: obj.id,
              actionTitle: action.titre,
              echeance: action.echeance,
              daysLeft,
              responsable: action.responsable
            });
          }
        }
      });
      if (obj.date_fin && obj.statut !== 'termine' && obj.statut !== 'abandonne') {
        const date = new Date(obj.date_fin);
        const daysLeft = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
        if (daysLeft <= 30 && daysLeft >= 0) {
          deadlines.push({
            objectiveTitle: obj.titre,
            objectiveId: obj.id,
            actionTitle: null,
            echeance: obj.date_fin,
            daysLeft,
            responsable: obj.responsable
          });
        }
      }
    });
    return deadlines.sort((a, b) => a.daysLeft - b.daysLeft);
  };

  // === Filtres ===

  const filteredObjectives = objectives.filter(o => {
    if (selectedCategory !== 'all' && o.categorie !== selectedCategory) return false;
    if (filterStatus !== 'all' && o.statut !== filterStatus) return false;
    if (filterPriority !== 'all' && o.priorite !== filterPriority) return false;
    return true;
  });

  // Chart data
  const categoryChartData = dashboard?.categories
    ? Object.entries(dashboard.categories).map(([code, data]) => ({
        name: data.label,
        value: data.total_objectives || 0,
        progress: data.avg_progress || 0,
        fill: data.color || '#6B7280'
      }))
    : [];

  const budgetData = objectives.reduce((acc, obj) => {
    if (!obj.parent_id && obj.budget > 0) {
      const cat = categories[obj.categorie]?.label || obj.categorie;
      const existing = acc.find(a => a.name === cat);
      if (existing) {
        existing.budget += obj.budget || 0;
        existing.utilise += obj.budget_utilise || 0;
      } else {
        acc.push({
          name: cat,
          budget: obj.budget || 0,
          utilise: obj.budget_utilise || 0,
          fill: categories[obj.categorie]?.color || '#6B7280'
        });
      }
    }
    return acc;
  }, []);

  // === Formulaire objectif (partagé entre création et édition) ===

  const ObjectiveForm = ({ data, setData, onSubmit, submitLabel }) => (
    <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Catégorie</Label>
          <Select value={data.categorie} onValueChange={(v) => setData({ ...data, categorie: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(categories).map(([code, config]) => (
                <SelectItem key={code} value={code}>
                  <span className="flex items-center gap-2">
                    {getCategoryIcon(code)}
                    {config.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Priorité</Label>
          <Select value={data.priorite} onValueChange={(v) => setData({ ...data, priorite: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="low">Basse</SelectItem>
              <SelectItem value="medium">Moyenne</SelectItem>
              <SelectItem value="high">Haute</SelectItem>
              <SelectItem value="critical">Critique</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Titre *</Label>
          <Input
            value={data.titre}
            onChange={(e) => setData({ ...data, titre: e.target.value })}
            placeholder="Ex: Réduire la consommation d'énergie"
          />
        </div>
        <div className="space-y-2">
          <Label>Statut</Label>
          <Select value={data.statut || 'en_cours'} onValueChange={(v) => setData({ ...data, statut: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="planifie">Planifié</SelectItem>
              <SelectItem value="en_cours">En cours</SelectItem>
              <SelectItem value="termine">Terminé</SelectItem>
              <SelectItem value="abandonne">Abandonné</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Description</Label>
        <Textarea
          value={data.description}
          onChange={(e) => setData({ ...data, description: e.target.value })}
          placeholder="Description détaillée de l'objectif"
          rows={2}
        />
      </div>

      <div className="grid grid-cols-4 gap-4">
        <div className="space-y-2">
          <Label>Indicateur</Label>
          <Input
            value={data.indicateur}
            onChange={(e) => setData({ ...data, indicateur: e.target.value })}
            placeholder="KWh, tCO2..."
          />
        </div>
        <div className="space-y-2">
          <Label>Unité</Label>
          <Input
            value={data.unite}
            onChange={(e) => setData({ ...data, unite: e.target.value })}
            placeholder="%"
          />
        </div>
        <div className="space-y-2">
          <Label>Valeur cible</Label>
          <Input
            type="number"
            value={data.valeur_cible}
            onChange={(e) => setData({ ...data, valeur_cible: parseFloat(e.target.value) || 0 })}
          />
        </div>
        <div className="space-y-2">
          <Label>Valeur actuelle</Label>
          <Input
            type="number"
            value={data.valeur_actuelle}
            onChange={(e) => setData({ ...data, valeur_actuelle: parseFloat(e.target.value) || 0 })}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Budget prévu (€)</Label>
          <Input
            type="number"
            value={data.budget || 0}
            onChange={(e) => setData({ ...data, budget: parseFloat(e.target.value) || 0 })}
            placeholder="0"
          />
        </div>
        <div className="space-y-2">
          <Label>Budget utilisé (€)</Label>
          <Input
            type="number"
            value={data.budget_utilise || 0}
            onChange={(e) => setData({ ...data, budget_utilise: parseFloat(e.target.value) || 0 })}
            placeholder="0"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Date début</Label>
          <Input
            type="date"
            value={data.date_debut || ''}
            onChange={(e) => setData({ ...data, date_debut: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label>Date fin</Label>
          <Input
            type="date"
            value={data.date_fin || ''}
            onChange={(e) => setData({ ...data, date_fin: e.target.value })}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Responsable</Label>
        <Select
          value={data.responsable_id || '_text'}
          onValueChange={(v) => {
            if (v === '_text') {
              setData({ ...data, responsable_id: '', responsable: data.responsable || '' });
            } else {
              const emp = employees.find(e => e.id === v);
              if (emp) {
                setData({ ...data, responsable_id: v, responsable: `${emp.prenom} ${emp.nom}` });
              }
            }
          }}
        >
          <SelectTrigger><SelectValue placeholder="Sélectionner un employé" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_text">Saisie libre</SelectItem>
            {employees.map(emp => (
              <SelectItem key={emp.id} value={emp.id}>
                {emp.prenom} {emp.nom} {emp.poste ? `- ${emp.poste}` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(!data.responsable_id || data.responsable_id === '') && (
          <Input
            value={data.responsable || ''}
            onChange={(e) => setData({ ...data, responsable: e.target.value })}
            placeholder="Nom du responsable"
            className="mt-2"
          />
        )}
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={() => { setShowAddDialog(false); setShowEditDialog(false); }}>Annuler</Button>
        <Button onClick={onSubmit} className="bg-[#0F5156] hover:bg-[#0d464a]">{submitLabel}</Button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F5156]"></div>
      </div>
    );
  }

  const upcomingDeadlines = getUpcomingDeadlines();

  return (
    <div className="space-y-6 animate-fade-in" data-testid="rse-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">RSE</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Responsabilité Sociétale de l'Entreprise
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={fetchData} className="gap-2" size="sm">
            <RefreshCw className="w-4 h-4" />
            Actualiser
          </Button>
          <Button variant="outline" onClick={() => handleExport('csv')} className="gap-2" size="sm">
            <FileSpreadsheet className="w-4 h-4" />
            CSV
          </Button>
          <Button variant="outline" onClick={() => handleExport('pdf')} className="gap-2" size="sm">
            <FileText className="w-4 h-4" />
            PDF
          </Button>
          <Button
            variant="outline"
            onClick={() => window.location.href = '/admin?section=rse-categories'}
            className="gap-2"
            size="sm"
          >
            <Settings className="w-4 h-4" />
            Catégories (Admin)
          </Button>
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="bg-[#0F5156] hover:bg-[#0d464a] gap-2" size="sm">
                <Plus className="w-4 h-4" />
                Nouvel objectif
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Nouvel objectif RSE</DialogTitle>
              </DialogHeader>
              <ObjectiveForm
                data={newObjective}
                setData={setNewObjective}
                onSubmit={handleCreateObjective}
                submitLabel="Créer"
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard">Tableau de bord</TabsTrigger>
          <TabsTrigger value="objectives">Objectifs ({objectives.length})</TabsTrigger>
          <TabsTrigger value="budget">Budget</TabsTrigger>
        </TabsList>

        {/* === TAB DASHBOARD === */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* KPI Cards */}
          {dashboard && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-[#0F5156] to-[#0d464a] text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm opacity-80">Progression globale</p>
                      <p className="text-3xl font-bold">{Math.round(dashboard.overall?.progress || 0)}%</p>
                    </div>
                    <BarChart3 className="w-10 h-10 opacity-50" />
                  </div>
                  <Progress value={dashboard.overall?.progress || 0} className="mt-4 bg-white/20" />
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Objectifs</p>
                      <p className="text-2xl font-bold">
                        {dashboard.overall?.completed_objectives || 0}/{dashboard.overall?.total_objectives || 0}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">terminés</p>
                    </div>
                    <Target className="w-8 h-8 text-[#0F5156]" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Actions réalisées</p>
                      <p className="text-2xl font-bold">
                        {dashboard.overall?.completed_actions || 0}/{dashboard.overall?.total_actions || 0}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">complétées</p>
                    </div>
                    <CheckCircle className="w-8 h-8 text-emerald-500" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Budget total</p>
                      <p className="text-2xl font-bold">
                        {objectives.reduce((s, o) => s + (o.budget || 0), 0).toLocaleString('fr-FR')} €
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {objectives.reduce((s, o) => s + (o.budget_utilise || 0), 0).toLocaleString('fr-FR')} € utilisés
                      </p>
                    </div>
                    <Wallet className="w-8 h-8 text-amber-500" />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Categories Progress + Chart */}
          {dashboard?.categories && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg">Progression par catégorie</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {Object.entries(dashboard.categories).map(([code, data]) => (
                      <div key={code} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="p-2 rounded-lg" style={{ backgroundColor: `${data.color}20` }}>
                              {getCategoryIcon(code)}
                            </div>
                            <div>
                              <span className="font-medium">{data.label}</span>
                              <p className="text-xs text-slate-500">
                                {data.completed_actions}/{data.total_actions} actions
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="font-bold" style={{ color: data.color }}>{Math.round(data.avg_progress)}%</span>
                            <p className="text-xs text-slate-500">
                              {data.completed_objectives}/{data.total_objectives} objectifs
                            </p>
                          </div>
                        </div>
                        <Progress value={data.avg_progress} className="h-2" />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Répartition</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={categoryChartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={70}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {categoryChartData.map((entry, index) => (
                            <Cell key={index} fill={entry.fill} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name) => [`${value} objectifs`, name]} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex flex-wrap justify-center gap-3 mt-2">
                    {categoryChartData.map((item, index) => (
                      <div key={index} className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.fill }} />
                        <span className="text-xs">{item.name} ({item.value})</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Upcoming deadlines */}
          {upcomingDeadlines.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  Échéances à venir (30 jours)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {upcomingDeadlines.slice(0, 10).map((deadline, i) => (
                    <div key={i} className={`flex items-center justify-between p-3 rounded-lg border ${
                      deadline.daysLeft <= 7 ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
                    }`}>
                      <div className="flex items-center gap-3">
                        <Clock className={`w-4 h-4 ${deadline.daysLeft <= 7 ? 'text-red-500' : 'text-amber-500'}`} />
                        <div>
                          <p className="font-medium text-sm">
                            {deadline.actionTitle || deadline.objectiveTitle}
                          </p>
                          {deadline.actionTitle && (
                            <p className="text-xs text-slate-500">Objectif : {deadline.objectiveTitle}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className={deadline.daysLeft <= 7 ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}>
                          {deadline.daysLeft === 0 ? "Aujourd'hui" : `${deadline.daysLeft} jour${deadline.daysLeft > 1 ? 's' : ''}`}
                        </Badge>
                        {deadline.responsable && (
                          <p className="text-xs text-slate-500 mt-1">{deadline.responsable}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* === TAB OBJECTIFS === */}
        <TabsContent value="objectives" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-3">
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Catégorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes catégories</SelectItem>
                {Object.entries(categories).map(([code, config]) => (
                  <SelectItem key={code} value={code}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous statuts</SelectItem>
                <SelectItem value="planifie">Planifié</SelectItem>
                <SelectItem value="en_cours">En cours</SelectItem>
                <SelectItem value="termine">Terminé</SelectItem>
                <SelectItem value="abandonne">Abandonné</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterPriority} onValueChange={setFilterPriority}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Priorité" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes priorités</SelectItem>
                <SelectItem value="critical">Critique</SelectItem>
                <SelectItem value="high">Haute</SelectItem>
                <SelectItem value="medium">Moyenne</SelectItem>
                <SelectItem value="low">Basse</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {filteredObjectives.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12 text-slate-500">
                <Target className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Aucun objectif RSE trouvé</p>
                <Button onClick={() => setShowAddDialog(true)} variant="outline" className="mt-4">
                  <Plus className="w-4 h-4 mr-2" />
                  Créer un objectif
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredObjectives.map((objective) => (
                <Card key={objective.id} className="overflow-hidden">
                  {/* Objective Header */}
                  <div
                    className="p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                    onClick={() => toggleExpand(objective.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {expandedObjectives.has(objective.id) ? (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        )}
                        <div
                          className="p-2 rounded-lg"
                          style={{ backgroundColor: `${categories[objective.categorie]?.color || '#6B7280'}20` }}
                        >
                          {getCategoryIcon(objective.categorie)}
                        </div>
                        <div>
                          <h3 className="font-medium">{objective.titre}</h3>
                          <div className="flex items-center gap-2 text-sm text-slate-500">
                            <span>{categories[objective.categorie]?.label || objective.categorie}</span>
                            {objective.responsable && <span>• {objective.responsable}</span>}
                            {objective.date_fin && <span>• Échéance : {objective.date_fin}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {getPriorityBadge(objective.priorite)}
                        {getStatusBadge(objective.statut)}
                        <div className="text-right min-w-[80px]">
                          <p className="text-lg font-bold" style={{ color: categories[objective.categorie]?.color }}>
                            {Math.round(objective.progress || 0)}%
                          </p>
                          <p className="text-xs text-slate-500">
                            {objective.actions?.length || 0} actions
                          </p>
                        </div>
                      </div>
                    </div>
                    <Progress value={objective.progress || 0} className="mt-3 h-2" />
                  </div>

                  {/* Expanded Content */}
                  {expandedObjectives.has(objective.id) && (
                    <div className="p-4 border-t bg-white dark:bg-slate-900">
                      {/* Description + KPI */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          {objective.description && (
                            <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">{objective.description}</p>
                          )}
                          {/* KPI indicator */}
                          {objective.indicateur && (
                            <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                              <p className="text-xs text-slate-500 mb-1">Indicateur : {objective.indicateur}</p>
                              <div className="flex items-center gap-2">
                                <span className="text-lg font-bold">
                                  {objective.valeur_actuelle || 0}
                                </span>
                                <span className="text-sm text-slate-500">/ {objective.valeur_cible || 0} {objective.unite}</span>
                              </div>
                              {objective.valeur_cible > 0 && (
                                <Progress
                                  value={Math.min(100, ((objective.valeur_actuelle || 0) / objective.valeur_cible) * 100)}
                                  className="h-2 mt-2"
                                />
                              )}
                            </div>
                          )}
                        </div>
                        <div className="space-y-2">
                          {/* Budget */}
                          {(objective.budget > 0) && (
                            <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                              <p className="text-xs text-slate-500 mb-1">Budget</p>
                              <div className="flex items-center justify-between">
                                <span className="text-lg font-bold">
                                  {(objective.budget_utilise || 0).toLocaleString('fr-FR')} €
                                </span>
                                <span className="text-sm text-slate-500">
                                  / {(objective.budget || 0).toLocaleString('fr-FR')} €
                                </span>
                              </div>
                              <Progress
                                value={objective.budget > 0 ? Math.min(100, ((objective.budget_utilise || 0) / objective.budget) * 100) : 0}
                                className="h-2 mt-2"
                              />
                              {objective.budget > 0 && objective.budget_utilise > objective.budget && (
                                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                  <AlertTriangle className="w-3 h-3" /> Dépassement de budget
                                </p>
                              )}
                            </div>
                          )}
                          {/* Dates */}
                          {(objective.date_debut || objective.date_fin) && (
                            <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-slate-400" />
                              <span className="text-sm">
                                {objective.date_debut || '?'} → {objective.date_fin || '?'}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Actions Checklist */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-sm">Actions ({objective.actions?.length || 0})</h4>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedObjective(objective);
                              setShowActionDialog(true);
                            }}
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            Ajouter
                          </Button>
                        </div>

                        {(!objective.actions || objective.actions.length === 0) ? (
                          <p className="text-sm text-slate-500 italic">Aucune action définie</p>
                        ) : (
                          objective.actions.map((action, index) => (
                            <div
                              key={action.id || index}
                              className={`flex items-start gap-3 p-3 rounded-lg border ${
                                action.completed ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-950 dark:border-emerald-800' : 'bg-white dark:bg-slate-800'
                              }`}
                            >
                              <Checkbox
                                checked={action.completed}
                                onCheckedChange={(checked) => handleToggleAction(objective.id, index, checked)}
                              />
                              <div className="flex-1 min-w-0">
                                <p className={`font-medium text-sm ${action.completed ? 'line-through text-slate-400' : ''}`}>
                                  {action.titre}
                                </p>
                                {action.description && (
                                  <p className="text-xs text-slate-500 mt-1">{action.description}</p>
                                )}
                                <div className="flex gap-4 mt-1 text-xs text-slate-400">
                                  {action.responsable && <span>Resp: {action.responsable}</span>}
                                  {action.echeance && (
                                    <span className={
                                      !action.completed && new Date(action.echeance) < new Date() ? 'text-red-500 font-medium' : ''
                                    }>
                                      Échéance: {action.echeance}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                {action.completed && <CheckCircle className="w-4 h-4 text-emerald-500" />}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedObjective(objective);
                                    setEditingAction({ ...action });
                                    setEditingActionIndex(index);
                                    setShowEditActionDialog(true);
                                  }}
                                >
                                  <Edit className="w-3 h-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteAction(objective.id, index);
                                  }}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>

                      {/* Objective toolbar */}
                      <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingObjective({ ...objective });
                            setShowEditDialog(true);
                          }}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Modifier
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-800"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteObjective(objective.id);
                          }}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Supprimer
                        </Button>
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* === TAB BUDGET === */}
        <TabsContent value="budget" className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-6">
                <p className="text-sm text-slate-500">Budget total</p>
                <p className="text-2xl font-bold">
                  {objectives.reduce((s, o) => s + (o.budget || 0), 0).toLocaleString('fr-FR')} €
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <p className="text-sm text-slate-500">Budget utilisé</p>
                <p className="text-2xl font-bold text-amber-600">
                  {objectives.reduce((s, o) => s + (o.budget_utilise || 0), 0).toLocaleString('fr-FR')} €
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <p className="text-sm text-slate-500">Budget restant</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {(objectives.reduce((s, o) => s + (o.budget || 0), 0) -
                    objectives.reduce((s, o) => s + (o.budget_utilise || 0), 0)).toLocaleString('fr-FR')} €
                </p>
              </CardContent>
            </Card>
          </div>

          {budgetData.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Budget par catégorie</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={budgetData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(value) => `${value.toLocaleString('fr-FR')} €`} />
                      <Bar dataKey="budget" name="Budget prévu" fill="#0F5156" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="utilise" name="Utilisé" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="text-center py-12 text-slate-500">
                <Wallet className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Aucun budget défini</p>
                <p className="text-sm mt-2">Ajoutez un budget à vos objectifs RSE pour suivre les dépenses</p>
              </CardContent>
            </Card>
          )}

          {/* Budget detail per objective */}
          {objectives.filter(o => o.budget > 0).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Détail par objectif</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {objectives.filter(o => o.budget > 0 && !o.parent_id).map(obj => {
                    const pct = obj.budget > 0 ? Math.round((obj.budget_utilise || 0) / obj.budget * 100) : 0;
                    return (
                      <div key={obj.id} className="flex items-center gap-4 p-3 rounded-lg border">
                        <div className="p-2 rounded-lg" style={{ backgroundColor: `${categories[obj.categorie]?.color || '#6B7280'}20` }}>
                          {getCategoryIcon(obj.categorie)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{obj.titre}</p>
                          <Progress value={Math.min(100, pct)} className="h-2 mt-1" />
                        </div>
                        <div className="text-right whitespace-nowrap">
                          <p className="font-bold text-sm">{(obj.budget_utilise || 0).toLocaleString('fr-FR')} €</p>
                          <p className="text-xs text-slate-500">/ {(obj.budget || 0).toLocaleString('fr-FR')} €</p>
                        </div>
                        <Badge className={pct > 100 ? 'bg-red-100 text-red-800' : pct > 80 ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}>
                          {pct}%
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* === DIALOGS === */}

      {/* Edit Objective Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Modifier l'objectif RSE</DialogTitle>
          </DialogHeader>
          {editingObjective && (
            <ObjectiveForm
              data={editingObjective}
              setData={setEditingObjective}
              onSubmit={handleEditObjective}
              submitLabel="Enregistrer"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Add Action Dialog */}
      <Dialog open={showActionDialog} onOpenChange={setShowActionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter une action</DialogTitle>
            {selectedObjective && (
              <p className="text-sm text-slate-500">{selectedObjective.titre}</p>
            )}
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Titre *</Label>
              <Input
                value={newAction.titre}
                onChange={(e) => setNewAction({ ...newAction, titre: e.target.value })}
                placeholder="Ex: Installer des LED"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={newAction.description}
                onChange={(e) => setNewAction({ ...newAction, description: e.target.value })}
                placeholder="Détails de l'action"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Responsable</Label>
                <Select
                  value={newAction.responsable || '_empty'}
                  onValueChange={(v) => {
                    if (v === '_empty' || v === '_text') {
                      setNewAction({ ...newAction, responsable: '' });
                    } else {
                      const emp = employees.find(e => e.id === v);
                      if (emp) setNewAction({ ...newAction, responsable: `${emp.prenom} ${emp.nom}` });
                    }
                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_text">Saisie libre</SelectItem>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.prenom} {emp.nom}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={newAction.responsable}
                  onChange={(e) => setNewAction({ ...newAction, responsable: e.target.value })}
                  placeholder="Nom du responsable"
                  className="mt-1"
                />
              </div>
              <div className="space-y-2">
                <Label>Échéance</Label>
                <Input
                  type="date"
                  value={newAction.echeance}
                  onChange={(e) => setNewAction({ ...newAction, echeance: e.target.value })}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowActionDialog(false)}>Annuler</Button>
              <Button onClick={handleAddAction} className="bg-[#0F5156] hover:bg-[#0d464a]">Ajouter</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Action Dialog */}
      <Dialog open={showEditActionDialog} onOpenChange={setShowEditActionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier l'action</DialogTitle>
          </DialogHeader>
          {editingAction && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Titre *</Label>
                <Input
                  value={editingAction.titre}
                  onChange={(e) => setEditingAction({ ...editingAction, titre: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={editingAction.description || ''}
                  onChange={(e) => setEditingAction({ ...editingAction, description: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Responsable</Label>
                  <Input
                    value={editingAction.responsable || ''}
                    onChange={(e) => setEditingAction({ ...editingAction, responsable: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Échéance</Label>
                  <Input
                    type="date"
                    value={editingAction.echeance || ''}
                    onChange={(e) => setEditingAction({ ...editingAction, echeance: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowEditActionDialog(false)}>Annuler</Button>
                <Button onClick={handleEditAction} className="bg-[#0F5156] hover:bg-[#0d464a]">Enregistrer</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Categories are now managed in Admin */}
    </div>
  );
};

export default RSE;
